import * as React from "react";
import { Link } from "~/components/ui/link";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion, type MotionStyle, type TargetAndTransition } from "motion/react";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { IconButton } from "~/components/ui/icon-button";
import { NavButton, SubnavPanelShell, useActiveSubnav, SUBNAV_GUTTER_CLOSE_DELAY, type SubnavId } from "~/components/header/full-header/subnavigation/common";
import { useMediaQuery } from "~/lib/use-media-query";
import ProductsNavItem from "~/components/header/full-header/subnavigation/ProductsNavItem";
import PersonalUseNavItem from "~/components/header/full-header/subnavigation/PersonalUseNavItem";
import FleetNavItem from "~/components/header/full-header/subnavigation/FleetNavItem";
import ResourcesNavItem from "~/components/header/full-header/subnavigation/ResourcesNavItem";
import { SlideMenu } from "~/components/header/collapsed-header/subnavigation/SlideMenu";
import { LocaleDialog } from "~/components/header/LocaleDialog";
import { FleetHeaderContext } from "~/components/header/header-mode";
import { useNavHiddenPastHero } from "~/components/header/use-nav-hidden-past-hero";
import { useToggleEmbeddedCart } from "~/lib/use-embedded-cart";
import { useLocalePreference, LANGUAGE_ITEMS } from "~/lib/use-locale-preference";
import { useCurrencyPreference } from "~/lib/use-currency-preference";
import wordmarkBlue from "~/assets/logos/bouncie-wordmark-blue.svg";
import wordmarkWhite from "~/assets/logos/bouncie-wordmark-white.svg";
import fleetSolutionsLogo from "~/assets/logos/bouncie-fleet-solutions-white.svg";
import { RiMenuLine, RiShoppingCart2Line, RiGlobalLine, RiArrowDownSLine } from "@remixicon/react";
import { HELP_CENTER_URL, WEB_APP_URL } from "~/lib/external-links";

export interface HeaderContentProps {
    // Whether this instance is capable of a transparent, white-on-video look
    // at rest — the logo/text crossfade to the solid look while a subnav is open.
    // Always false for a header that should never appear transparent.
    transparent: boolean;
    // Scopes the header to the .fleet (dark) tokens and swaps in the Fleet
    // Solutions logo and the Contact Sales / Build Quote CTAs.
    fleet?: boolean;
    // Extra classes for the outer element — e.g. fixed positioning for a floating header.
    wrapperClassName?: string;
    // Framer Motion style overrides on the outer element — e.g. a scroll-driven `y`.
    style?: MotionStyle;
    // Ref to the outer element, e.g. so a caller can measure its rendered height.
    headerRef?: (el: HTMLElement | null) => void;
    // Fires whenever the subnav or mobile menu open state changes.
    onOpenChange?: (open: boolean) => void;
    // Hides this instance from the accessibility tree and removes it from the
    // tab order — e.g. a floating follow header while it's off-screen, so it
    // doesn't register as a second "Main navigation" landmark or a hidden focus target.
    ariaHidden?: boolean;
}

// Bouncie wordmark SVGs share the same 1500x308 viewBox, so a single aspect
// ratio lets the logo crossfade + resize without distorting either asset.
const WORDMARK_ASPECT = 1500 / 308;

export function HeaderContent({
    transparent,
    fleet = false,
    wrapperClassName,
    style,
    headerRef,
    onOpenChange,
    ariaHidden = false,
}: HeaderContentProps) {
    const { t } = useTranslation("common");
    const toggleCart = useToggleEmbeddedCart();
    const [locale] = useLocalePreference();
    const [currency] = useCurrencyPreference();
    const [menuOpen, setMenuOpen] = React.useState(false);
    const close = () => setMenuOpen(false);
    const [localeDialogOpen, setLocaleDialogOpen] = React.useState(false);

    const { active, openNow, closeNow, closeWithDelay, openWithDelay, cancelPendingOpen, cancelPendingClose } = useActiveSubnav();
    const subnavOpen = active !== null;
    // `active` is already null while the panel plays its closing animation, so the
    // panel renders the last menu that was open rather than blanking on the way out.
    const [lastActive, setLastActive] = React.useState<SubnavId | null>(null);
    if (active !== null && active !== lastActive) setLastActive(active);
    const renderedSubnav = active ?? lastActive;
    // Tablets in the full (non-hamburger) header range often lack real hover.
    // Without it, a tap on a nav link opens/closes its subnav instead of navigating.
    const canHover = useMediaQuery("(hover: hover) and (pointer: fine)");
    const navRef = React.useRef<HTMLElement>(null);
    // Fleet's sticky header drops its nav items past the hero, until the next scroll up.
    const navHidden = useNavHiddenPastHero(fleet, navRef, subnavOpen);
    const reduceMotion = useReducedMotion();
    // On a hover-capable device, hover alone drives the panel — a real mouse click
    // would otherwise close it while the pointer is still over the trigger, and the
    // next pointer move reopens it via onMouseEnter. `detail` is 0 for a click
    // synthesized from Enter/Space, so keyboard users can still toggle it there.
    const handleTriggerClick = (id: SubnavId) => (e: React.MouseEvent) => {
        if (canHover && e.detail !== 0) return;
        if (active === id) closeNow(); else openNow(id);
    };

    // The gutter strip below the panel (see SubnavPanelShell): landing there starts a
    // longer, dedicated close delay instead of closing immediately. Leaving it back
    // upward into the nav just cancels that — the nav's own containment already keeps
    // the panel open. Leaving it downward, into real page content, closes instantly:
    // past the gutter, overshoot is no longer ambiguous.
    const handleGutterEnter = () => {
        if (active) closeWithDelay(active, SUBNAV_GUTTER_CLOSE_DELAY);
    };
    const handleGutterLeave = (e: React.MouseEvent) => {
        cancelPendingClose();
        if (!navRef.current?.contains(e.relatedTarget as Node)) closeNow();
    };

    // A transparent header temporarily borrows the solid look while a subnav is open.
    const showSolid = !transparent || subnavOpen;
    // Icon controls sit on a dark surface in fleet mode as well as when transparent.
    const onDarkSurface = fleet || !showSolid;
    const iconVariant = onDarkSurface ? "ghost" : "tertiary";

    React.useEffect(() => {
        onOpenChange?.(subnavOpen || menuOpen);
    }, [subnavOpen, menuOpen, onOpenChange]);

    // Lock body scroll while the overlay is open.
    React.useEffect(() => {
        if (menuOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
    }, [menuOpen]);

    // On touch devices, taps toggle the subnav instead of hover, so there's no
    // mouseleave to close it — close on the next tap outside the nav instead.
    React.useEffect(() => {
        if (canHover || !subnavOpen) return;
        function onPointerDown(e: PointerEvent) {
            if (!navRef.current?.contains(e.target as Node)) closeNow();
        }
        document.addEventListener("pointerdown", onPointerDown);
        return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [canHover, subnavOpen, closeNow]);

    // Fixed regardless of theme — Figma's Transparent and Fill • Light themes both
    // size the wordmark at 32px tall; only the color crossfades when a subnav opens.
    const logoHeight = 32;
    const logoWidth = logoHeight * WORDMARK_ASPECT;

    const logo = fleet ? (
        <div className="mx-auto flex shrink-0 items-center lg:mx-0">
            <Link to="/" aria-label={t("brand.fleet_solutions")}>
                <img src={fleetSolutionsLogo} alt={t("brand.fleet_solutions")} className="h-6 w-auto lg:h-8 lg:max-xl:hidden" />
                {/* At lg the lockup only fits while the nav is hidden, so it's masked to its "Bouncie"
                    (the first 126.79px of 293.12px) and reveals the rest behind a 50px fade. */}
                <span className="relative hidden h-8 w-[126.79px] lg:max-xl:block">
                    <motion.img
                        src={fleetSolutionsLogo}
                        alt={t("brand.fleet_solutions")}
                        className="absolute top-0 left-0 h-8 w-auto max-w-none"
                        style={{ maskImage: "linear-gradient(to right, #000 max(126.79px, calc(var(--reveal) - 50px)), transparent var(--reveal))" }}
                        initial={false}
                        animate={{ "--reveal": navHidden ? "343.12px" : "126.79px" } as TargetAndTransition}
                        // Slides open only once the nav has faded out (240ms); the nav waits for it to close.
                        transition={reduceMotion ? { duration: 0 } : { duration: 0.4, delay: navHidden ? 0.24 : 0, ease: "easeInOut" }}
                    />
                </span>
            </Link>
        </div>
    ) : (
        <div className="mx-auto flex shrink-0 items-center lg:mx-0">
            <Link to="/" aria-label="Bouncie">
                <div className="relative shrink-0" style={{ height: logoHeight, width: logoWidth }}>
                    <motion.img
                        src={wordmarkWhite}
                        alt="Bouncie"
                        className="absolute inset-0 h-full w-full"
                        initial={{ opacity: showSolid ? 0 : 1 }}
                        animate={{ opacity: showSolid ? 0 : 1 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                    />
                    <motion.img
                        src={wordmarkBlue}
                        alt="Bouncie"
                        className="absolute inset-0 h-full w-full"
                        initial={{ opacity: showSolid ? 1 : 0 }}
                        animate={{ opacity: showSolid ? 1 : 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                    />
                </div>
            </Link>
        </div>
    );

    const nav = (
        <nav
            ref={navRef}
            aria-label={t("nav.main_navigation")}
            // Fleet's items sit closer than the default, matching Figma's label positions per breakpoint.
            className={cn(
                "mx-auto hidden items-center justify-between gap-6 self-stretch lg:flex",
                fleet && "transition-[opacity,visibility] duration-240 lg:gap-3 xl:gap-[15px]",
                // At lg the nav waits for the logo to finish closing before fading back in.
                navHidden ? "invisible opacity-0" : fleet && "motion-safe:lg:max-xl:delay-400",
            )}
        >
            <div className="flex" onMouseEnter={canHover ? () => openWithDelay("products") : undefined} onMouseLeave={canHover ? cancelPendingOpen : undefined}>
                <NavButton open={active === "products"} anySubnavOpen={subnavOpen} id="products" label={t("nav.products")} onClick={handleTriggerClick("products")}/>
            </div>
            <div className="flex" onMouseEnter={canHover ? () => openWithDelay("personal-use") : undefined} onMouseLeave={canHover ? cancelPendingOpen : undefined}>
                <NavButton open={active === "personal-use"} anySubnavOpen={subnavOpen} id="personal-use" label={t("nav.personal_use")} onClick={handleTriggerClick("personal-use")}/>
            </div>
            <div className="flex" onMouseEnter={canHover ? () => openWithDelay("fleet") : undefined} onMouseLeave={canHover ? cancelPendingOpen : undefined}>
                <NavButton open={active === "fleet"} anySubnavOpen={subnavOpen} id="fleet" label={t("nav.fleet")} onClick={handleTriggerClick("fleet")}/>
            </div>
            <div className="flex" onMouseEnter={canHover ? () => openWithDelay("resources") : undefined} onMouseLeave={canHover ? cancelPendingOpen : undefined}>
                <NavButton open={active === "resources"} anySubnavOpen={subnavOpen} id="resources" label={t("nav.resources")} onClick={handleTriggerClick("resources")}/>
            </div>
            {/* One shell with a fixed key, so switching menus swaps its contents
                instead of unmounting a panel and mounting another. It stays a
                descendant of the header on purpose: the close-on-leave lives on
                the header itself, so hovering anywhere in the header — or the
                open panel — still counts as inside. */}
            <AnimatePresence>
                {subnavOpen && (
                    <SubnavPanelShell
                        key="subnav-panel"
                        onGutterEnter={canHover ? handleGutterEnter : undefined}
                        onGutterLeave={canHover ? handleGutterLeave : undefined}
                    >
                        {/* Crossfades menu-to-menu swaps — the shell's own open/close fade
                            (common.tsx) only plays on mount/unmount, so without this, switching
                            from one already-open menu to another replaced the content instantly. */}
                        <AnimatePresence initial={false}>
                            <motion.div
                                key={renderedSubnav}
                                style={{ gridArea: "1 / 1" }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15, ease: "easeInOut" }}
                            >
                                {renderedSubnav === "products" && <ProductsNavItem onNavigate={closeNow} />}
                                {renderedSubnav === "personal-use" && <PersonalUseNavItem onNavigate={closeNow} />}
                                {renderedSubnav === "fleet" && <FleetNavItem onNavigate={closeNow} />}
                                {renderedSubnav === "resources" && <ResourcesNavItem onNavigate={closeNow} />}
                            </motion.div>
                        </AnimatePresence>
                    </SubnavPanelShell>
                )}
            </AnimatePresence>
        </nav>
    );

    const hamburger = (
        <div className="lg:hidden">
            <IconButton
                icon={RiMenuLine}
                onClick={() => setMenuOpen(true)}
                aria-label={t("nav.open_menu")}
                variant={iconVariant}
                iconClassName="h-6 w-6"
            />
        </div>
    );

    // Figma's nav CTAs are fixed 154x44 with 16px bold text, unlike the shared Button sizes.
    // At lg, Contact Sales hugs its label to make room for the nav.
    const fleetCtaClassName = "h-11 w-[154px] px-4 py-3 text-base leading-5";

    const actions = (
        <div className="flex shrink-0 items-center justify-end gap-4">
            {fleet && (
                <>
                    <Button asChild variant="secondary-alt" className={cn("hidden lg:inline-flex", fleetCtaClassName, "lg:max-xl:w-auto")}>
                        <Link data-sid="header-fleet-contact-sales" to="/contact">{t("nav.contact_sales")}</Link>
                    </Button>
                    <Button asChild variant="primary-alt" className={cn("hidden lg:inline-flex", fleetCtaClassName)}>
                        <Link data-sid="header-fleet-build-quote" to="/fleet/quote">{t("nav.build_quote")}</Link>
                    </Button>
                </>
            )}
            <button
                type="button"
                onClick={toggleCart}
                aria-label={t("cart")}
                className={cn(
                    "focus-visible:ring-ring inline-flex h-12 w-fit shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full pr-4 pl-3 transition-colors duration-200 ease-out focus-visible:ring-1 focus-visible:outline-none",
                    onDarkSurface ? "text-white hover:bg-white hover:text-bouncie-500" : "text-text-heading hover:bg-bouncie-100",
                    // Fleet desktop swaps the cart for the sales CTAs.
                    fleet && "lg:hidden",
                )}
            >
                <RiShoppingCart2Line className="h-4 w-4" />
                <span className="font-display hidden text-base font-bold tracking-[0.16px] lg:inline">
                    {t("nav.cart")}
                </span>
            </button>
        </div>
    );

    // A slim utility strip above the main bar — cross-link to the app, support, and the
    // locale/currency picker (moved here from the main bar's icon-only trigger). Figma's
    // TopNav (home page, node 4672:38703) shows this same solid strip sitting above the
    // hero unconditionally, so it renders regardless of the `transparent` variant.
    // The fill spans the full viewport width; `.wrap` goes on an inner div so only the
    // content — not the background — is inset to align with the page content below it.
    const utilityBar = (
        <div className="bg-bg-alt">
            <div className="wrap text-text-body-secondary fleet:text-text-body flex items-center justify-end gap-6 py-0.5 text-xs">
                <a href={WEB_APP_URL} className="hover:text-text-heading hidden p-1 transition-colors lg:block">
                    {t("nav.bouncie_app")}
                </a>
                <span aria-hidden className="bg-border fleet:bg-neutral-400 hidden h-5 w-px lg:block" />
                <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer" className="hover:text-text-heading flex items-center gap-1 p-1 transition-colors">
                    {t("nav.support")}
                    <RiArrowDownSLine className="h-4 w-4" />
                </a>
                <span aria-hidden className="bg-border fleet:bg-neutral-400 h-5 w-px" />
                <button
                    type="button"
                    onClick={() => setLocaleDialogOpen(true)}
                    aria-label={t("nav.language")}
                    className="hover:text-text-heading flex cursor-pointer items-center gap-1 p-1 transition-colors"
                >
                    <RiGlobalLine className="h-[18px] w-[18px]" />
                    {LANGUAGE_ITEMS.find((item) => item.id === locale)?.title} • {currency.toUpperCase()}
                </button>
            </div>
        </div>
    );

    return (
        <FleetHeaderContext.Provider value={fleet}>
            <motion.header
                ref={headerRef}
                style={style}
                aria-hidden={ariaHidden || undefined}
                inert={ariaHidden}
                onMouseEnter={canHover ? cancelPendingClose : undefined}
                onMouseLeave={canHover ? () => { if (active) closeWithDelay(active); } : undefined}
                className={cn(
                    "relative z-40 w-full transition-[background-color,color] duration-300 ease-out",
                    showSolid ? "text-text-heading bg-nav-bg" : "text-white",
                    // Fleet's desktop header stays pinned while the page scrolls.
                    fleet && "fleet lg:sticky lg:top-0",
                    wrapperClassName,
                )}
            >
                {utilityBar}
                {/* Fleet's desktop bar is 16px of padding around its 44px CTAs, per Figma. At lg its
                    Figma gutter is 32px, 8px wider than wrap's. */}
                <div className={cn("wrap flex h-16 items-center gap-6 md:h-21", fleet && "lg:h-auto lg:py-4 lg:max-xl:px-2")}>
                    {hamburger}
                    {logo}
                    {nav}
                    {actions}
                </div>
                <SlideMenu menuOpen={menuOpen} close={close} onOpenLocaleDialog={() => setLocaleDialogOpen(true)} />
            </motion.header>
            <AnimatePresence>
                {subnavOpen && !ariaHidden && (
                    <motion.div
                        key="subnav-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.65 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        onClick={closeNow}
                        aria-hidden="true"
                        className="fixed inset-0 z-30 bg-neutral-800 backdrop-blur"
                    />
                )}
            </AnimatePresence>
            <LocaleDialog open={localeDialogOpen} onOpenChange={setLocaleDialogOpen} />
        </FleetHeaderContext.Provider>
    );
}
